import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { createArticle, updateArticle, getArticleStatus } from '../../Requests/ArticleRequests'
import { ButtonParticleLoader } from "../ParticleLoader";
import NoCreditsModal from '../Billing/NoCreditsModal'

export default function CreateArticleModal({ projectId, article = null, onClose = null, onSuccess = null }) {
    const [open, setOpen] = useState(true)
    const [loading, setLoading] = useState(false)
    const [pollingStatus, setPollingStatus] = useState(false)
    const [insufficientCredits, setInsufficientCredits] = useState(null)
    const { t } = useTranslation()
    const isEditMode = !!article
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        details: '',
        pages: 5,
        prompt: ''
    })
    
    // Research context state
    const [researchContext, setResearchContext] = useState({
        research_question: '',
        importance_rationale: '',
        participants_selection: '',
        data_collection_methods: '',
        data_analysis_methods: '',
        main_findings: '',
        main_conclusion: '',
        limitations: ''
    })

    // Pre-fill form when editing
    useEffect(() => {
        if (isEditMode && article) {
            setFormData({
                title: article.article_title || '',
                details: article.article_details || '',
                pages: article.article_pages || 5,
                prompt: article.article_prompt || ''
            })
            
            // Pre-fill research context if it exists
            if (article.research_context) {
                setResearchContext({
                    research_question: article.research_context.research_question || '',
                    importance_rationale: article.research_context.importance_rationale || '',
                    participants_selection: article.research_context.participants_selection || '',
                    data_collection_methods: article.research_context.data_collection_methods || '',
                    data_analysis_methods: article.research_context.data_analysis_methods || '',
                    main_findings: article.research_context.main_findings || '',
                    main_conclusion: article.research_context.main_conclusion || '',
                    limitations: article.research_context.limitations || ''
                })
            }
        }
    }, [isEditMode, article])

    const close = () => {
        setOpen(false);
        if (onClose) onClose()
    }

    const handleResearchContextChange = (field, value) => {
        setResearchContext(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const pollArticleStatus = async (articleId) => {
        try {
            const statusRes = await getArticleStatus(articleId)
            const article = statusRes.data.article
            
            if (article.status === 'completed') {
                setPollingStatus(false)
                if (onSuccess) onSuccess(article)
                close()
            } else if (article.status === 'failed') {
                setPollingStatus(false)
                setLoading(false)
                alert(t('articles.createArticleModal.creationFailed'))
            } else if (article.status === 'pending' || article.status === 'processing') {
                // Continue polling
                setTimeout(() => pollArticleStatus(articleId), 3000)
            }
        } catch (error) {
            console.error('Error checking article status:', error)
            setPollingStatus(false)
            setLoading(false)
            alert(t('articles.createArticleModal.statusError'))
        }
    }

    const handleSubmitArticle = (event) => {
        event.preventDefault();
        setLoading(true)
        
        // Check if any research context fields have content
        const hasResearchContext = Object.values(researchContext).some(value => value.trim() !== '')
        
        let data = {
            "article_title": formData.title,
            "article_details": formData.details,
            "article_pages": parseInt(formData.pages),
            "article_prompt": formData.prompt
        }
        
        // Add project_id only for create mode
        if (!isEditMode) {
            data.project_id = projectId
        }
        
        // Add research context if any fields are filled
        if (hasResearchContext) {
            data.research_context = researchContext
        }

        const apiCall = isEditMode 
            ? updateArticle(article.id, data)
            : createArticle(data)

        apiCall.then((res) => {
            // Check if the response indicates a background job (202 status)
            if (res.status === 202) {
                setPollingStatus(true)
                pollArticleStatus(res.data.article.id)
            } else {
                // Synchronous response - complete immediately
                if (onSuccess) onSuccess(res.data.article)
                close()
            }
        }).catch((error) => {
            console.error('Error saving article:', error)
            setLoading(false)
            
            // Check if error is due to insufficient credits
            try {
                const errorResponse = JSON.parse(error.request?.response)
                if (errorResponse?.message === "Insufficient credits") {
                    setInsufficientCredits(
                        <NoCreditsModal
                            title={t('CreditsModal.Title')}
                            body={t('CreditsModal.Body')}
                            continue={t('CreditsModal.Continue')}
                            navLink="/pricing/credits"
                            handleClose={() => setInsufficientCredits(null)}
                        />
                    )
                }
            } catch (parseError) {
                // If we can't parse the error response, continue with default behavior
                console.error('Error parsing response:', parseError)
            }
        })
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={close}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                        onClick={close}
                                    >
                                        <span className="sr-only">{t('articles.createArticleModal.close')}</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                            {isEditMode ? t('articles.createArticleModal.editTitle') : t('articles.createArticleModal.createTitle')}
                                        </Dialog.Title>
                                        <div className="mt-6">
                                            <form onSubmit={handleSubmitArticle} className="space-y-4">
                                                <div>
                                                    <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articles.createArticleModal.articleTitle')}<span className="text-red-600 ml-0.5">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="title"
                                                        id="title"
                                                        required
                                                        value={formData.title}
                                                        onChange={(e) => handleFormChange('title', e.target.value)}
                                                        className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                        placeholder={t('articles.createArticleModal.articleTitlePlaceholder')}
                                                    />
                                                </div>

                                                <div>
                                                    <label htmlFor="details" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articles.createArticleModal.articleDetails')}<span className="text-red-600 ml-0.5">*</span>
                                                    </label>
                                                    <textarea
                                                        name="details"
                                                        id="details"
                                                        rows={3}
                                                        required
                                                        value={formData.details}
                                                        onChange={(e) => handleFormChange('details', e.target.value)}
                                                        className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                        placeholder={t('articles.createArticleModal.articleDetailsPlaceholder')}
                                                    />
                                                </div>

                                                <div>
                                                    <label htmlFor="pages" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articles.createArticleModal.numberOfPages')}<span className="text-red-600 ml-0.5">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        name="pages"
                                                        id="pages"
                                                        min="1"
                                                        max="50"
                                                        required
                                                        value={formData.pages}
                                                        onChange={(e) => handleFormChange('pages', e.target.value)}
                                                        className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                    />
                                                </div>

                                                <div>
                                                    <label htmlFor="prompt" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articles.createArticleModal.articlePrompt')}
                                                    </label>
                                                    <textarea
                                                        name="prompt"
                                                        id="prompt"
                                                        rows={2}
                                                        value={formData.prompt}
                                                        onChange={(e) => handleFormChange('prompt', e.target.value)}
                                                        className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                        placeholder={t('articles.createArticleModal.articlePromptPlaceholder')}
                                                    />
                                                </div>

                                                {/* Research Context Section */}
                                                <div className="border-t pt-6">
                                                    <h4 className="text-base font-medium text-gray-900 mb-4">{t('articles.createArticleModal.researchContext')}</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.researchQuestion')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.research_question}
                                                                onChange={(e) => handleResearchContextChange('research_question', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.researchQuestionPlaceholder')}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.importanceRationale')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.importance_rationale}
                                                                onChange={(e) => handleResearchContextChange('importance_rationale', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.importanceRationalePlaceholder')}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.participantsSelection')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.participants_selection}
                                                                onChange={(e) => handleResearchContextChange('participants_selection', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.participantsSelectionPlaceholder')}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.dataCollectionMethods')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.data_collection_methods}
                                                                onChange={(e) => handleResearchContextChange('data_collection_methods', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.dataCollectionMethodsPlaceholder')}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.dataAnalysisMethods')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.data_analysis_methods}
                                                                onChange={(e) => handleResearchContextChange('data_analysis_methods', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.dataAnalysisMethodsPlaceholder')}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.mainFindings')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.main_findings}
                                                                onChange={(e) => handleResearchContextChange('main_findings', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.mainFindingsPlaceholder')}
                                                            />
                                                        </div>

                                                        <div className="md:col-span-2">
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.mainConclusion')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.main_conclusion}
                                                                onChange={(e) => handleResearchContextChange('main_conclusion', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.mainConclusionPlaceholder')}
                                                            />
                                                        </div>

                                                        <div className="md:col-span-2">
                                                            <label className="block text-sm font-medium leading-6 text-gray-900">
                                                                {t('articles.createArticleModal.limitations')}
                                                            </label>
                                                            <textarea
                                                                value={researchContext.limitations}
                                                                onChange={(e) => handleResearchContextChange('limitations', e.target.value)}
                                                                rows={2}
                                                                className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articles.createArticleModal.limitationsPlaceholder')}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* AI Generation Warning */}
                                                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
                                                    <div className="flex">
                                                        <div className="flex-shrink-0">
                                                            <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                        <div className="ml-3">
                                                            <h3 className="text-sm font-medium text-amber-800">
                                                                {t('articles.createArticleModal.aiWarning.title')}
                                                            </h3>
                                                            <div className="mt-2 text-sm text-amber-700">
                                                                <p>{t('articles.createArticleModal.aiWarning.description')}</p>
                                                                <ul className="mt-2 list-disc list-inside space-y-1">
                                                                    <li>{t('articles.createArticleModal.aiWarning.point1')}</li>
                                                                    <li>{t('articles.createArticleModal.aiWarning.point2')}</li>
                                                                    <li>{t('articles.createArticleModal.aiWarning.point3')}</li>
                                                                    <li>{t('articles.createArticleModal.aiWarning.point4')}</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                                    <button
                                                        type="submit"
                                                        disabled={loading}
                                                        className="inline-flex w-full justify-center rounded-md bg-plate-accent px-3 py-2 text-sm font-semibold text-plate-ink shadow-sm hover:bg-plate-accent-hover disabled:opacity-50 sm:ml-3 sm:w-auto"
                                                    >
                                                        {loading ? (
                                                            pollingStatus ? (
                                                                <div className="flex items-center gap-2">
                                                                    <ButtonParticleLoader />
                                                                    <span>{t('articles.createArticleModal.processing')}</span>
                                                                </div>
                                                            ) : (
                                                                <ButtonParticleLoader />
                                                            )
                                                        ) : (
                                                            isEditMode ? t('articles.createArticleModal.updateButton') : t('articles.createArticleModal.createButton')
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                        onClick={close}
                                                    >
                                                        {t('articles.createArticleModal.cancel')}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
            {/* No Credits Modal */}
            {insufficientCredits}
        </Transition.Root>
    )
}